import { appDataBaseSocket } from '@common/configs/app-database-socket';
import { componentName } from '@common/constants/shared';
import { useBloctoAction } from '@common/hooks/useBlocto';
import { useChatComponentActions } from '@common/hooks/useChatComponentActions';
import { fincodeActions } from '@common/hooks/useFincode';
import { useGptConversationAction } from '@common/hooks/useGptConversation';
import { useIapSubscription } from '@common/hooks/useIapSubscription';
import { useLineLoginAction } from '@common/hooks/useLineLoginAction';
import { useDodaCampusLoginAction } from '@common/hooks/useDodaCampusLoginAction';
import { useMapActions } from '@common/hooks/useMapActions';
import { useTorusLogin } from '@common/hooks/useTorusLogin';
import { useUploadFile } from '@common/hooks/useUploadFile';
import { appInfoSelector } from '@common/redux/selectors/app';
import { profileSelector } from '@common/redux/selectors/auth';
import { appLanguageSelector } from '@common/redux/selectors/language';
import { setError } from '@common/redux/slice/app';
import { updateLoggedUserSuccess } from '@common/redux/slice/auth';
import { stripePayment, univaPayPayment } from '@common/services/axiosIntance';
import { ILibraryComponent } from '@common/types/element';
import { onPressAction } from '@common/utils/handleActions/excuteAction';
import {
  handleBuyIap,
  showAlert,
  updateRecurringLoggedInUserDispatch,
} from '@common/utils/handleActions/func/func';
import { get } from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import appConfig from '../../../../appConfig.json';

export const useCustomProp = (props: ILibraryComponent) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const { locale } = useSelector(appLanguageSelector);
  const appInfo = useSelector(appInfoSelector);
  const profile = useSelector(profileSelector);
  const fileActions = useUploadFile();

  const auth = {
    ...profile,
    userId: profile?.id,
    databaseId: get(appInfo, 'metadata.auth.tableId'),
  };

  const handleSetError = (res: string) => {
    dispatch(setError({ message: res }));
  };

  const getCustomProp = () => {
    switch (props.componentName) {
      // case componentName.STRIPE_PAYMENT_SECURITY: {
      //   return {
      //     auth,
      //     locale,
      //     accountConnectId: appInfo?.accountConnectId,
      //     updateRecordRecordApi: updateLoggedInUserDispatch,
      //     handlePayment: stripePaymentSecurityActionApi,
      //   };
      // }
      case componentName.TORUS_LOGIN: {
        const { loginSuccessWithTorus } = useTorusLogin();
        return { loginSuccessWithTorus, locale };
      }
      case componentName.LIVESTREAM_CHAT: {
        return {
          auth: auth,
          appConfig: {
            appId: appInfo?.id,
            auth: appInfo?.metadata?.auth,
          },
          socketClient: appDataBaseSocket,
        };
      }
      case componentName.IN_APP_PURCHASE: {
        return {
          appId: appInfo?.id,
          profile: auth,
          handleBuyIap,
          updateLoggedUserSuccess,
          handleSetError,
        };
      }

      case componentName.STRIPE_PAYMENT:
      case componentName.STRIPE_SUBSCRIPTION: {
        return {
          stripePayment,
          onPressAction,
          paramsPayload: {
            appId: appInfo?.id,
          },
          test_mode: appInfo?.metadata?.stripe,
        };
      }
      case componentName.STRIPE_PAYMENT_V2: {
        return {
          appConfig,
        };
      }
      case componentName.UNIVAPAY:
      case componentName.UNIVAPAY_SUBSCRIPTION: {
        return {
          univaPayPayment,
          isConnected: appInfo?.isConnectUnivapay,
          paramsPayload: {
            appId: appInfo?.id,
          },
          recurring: profile?.recurring,
          onUpdateRecurringLoggedInUser: updateRecurringLoggedInUserDispatch,
          onPressAction,
          isLoggedInUser: !!profile,
        };
      }
      case componentName.FINCODE: {
        return {
          appid: appInfo?.id,
          isConnected:
            (!!appInfo?.metadata.fincode?.shopId &&
              !!appInfo.metadata.fincode.isVerified) ||
            !!appInfo?.metadata.fincode?.testMode?.shopId,
          auth,
          testModeShopId: appInfo?.metadata.fincode?.testMode?.shopId,
          ...fincodeActions,
        };
      }
      case componentName.PAYPAY: {
        return {
          appid: appInfo?.id,
          isConnected:
            (!!appInfo?.metadata.fincode?.shopId &&
              !!appInfo.metadata.fincode.isVerified) ||
            !!appInfo?.metadata.fincode?.testMode?.shopId,
          auth,
          testModeShopId: appInfo?.metadata.fincode?.testMode?.shopId,
          ...fincodeActions,
        };
      }
      case componentName.BLOCTO: {
        const actions = useBloctoAction();
        return actions;
      }
      case componentName.GPT_CONVERSATION: {
        const gptActions = useGptConversationAction(appInfo?.appId || '');
        return {
          appId: appInfo?.appId,
          gptActions,
          fileActions,
        };
      }
      case componentName.LINE_LOGIN: {
        const actions = useLineLoginAction();
        return actions;
      }
      case componentName.DODA_LOGIN: {
        const actions = useDodaCampusLoginAction();
        return actions;
      }
      case componentName.CHAT_COMPONENT: {
        const chatActions = useChatComponentActions({
          appId: appInfo?.appId || '',
        });
        return { chatActions, fileActions };
      }
      case componentName.MAP: {
        const mapActions = useMapActions();
        return { mapActions };
      }
      case componentName.IN_APP_PURCHASE_SUBSCRIPTION: {
        return useIapSubscription(appInfo?.appId || '');
      }
      case componentName.JPYC_TRANSFER: {
        return {
          relayerEndpoint: `${appConfig.baseUrl}/jpyc/gasless-relay`,
        };
      }
      default:
        return {};
    }
  };

  return {
    loading,
    getCustomProp,
  };
};
